import React, { useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  FlatList,
  ActivityIndicator,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { BottomTabNavigationProp } from '@react-navigation/bottom-tabs';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { useAuth } from '../../contexts/AuthContext';
import { showGuestAlert } from '../../utils/guestReadOnlyAlert';
import { impactLight } from '../../utils/haptics';
import { ensureChat } from '../../services/chatService';
import {
  searchNearbyUsers,
  searchByTargetBabyAgeMonths,
} from '../../services/searchService';
import { getBlockedUserIdsSet } from '../../services/userService';
import type { UserProfile } from '../../types/models';
import { formatBabyAgeMonthsLabel, getBabyAgeInMonths } from '../../utils/babyAge';
import { formatFuzzyProximityLabel } from '../../utils/location';
import type { MainStackParamList, MainTabParamList } from '../../navigation/types';

type Mode = 'nearby' | 'general';

export function SearchScreen() {
  const insets = useSafeAreaInsets();
  const { user, profile, logout } = useAuth();
  const tabNavigation = useNavigation<BottomTabNavigationProp<MainTabParamList, 'Search'>>();
  const [mode, setMode] = useState<Mode>('nearby');
  const [city, setCity] = useState(profile?.city ?? '');
  const [district, setDistrict] = useState(profile?.district ?? '');
  const [targetMonthsInput, setTargetMonthsInput] = useState('');
  const [results, setResults] = useState<UserProfile[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setCity(profile?.city ?? '');
    setDistrict(profile?.district ?? '');
  }, [profile?.city, profile?.district]);

  const targetMonths = parseInt(targetMonthsInput.trim(), 10);
  const canRunGeneral =
    !Number.isNaN(targetMonths) && targetMonths >= 0 && targetMonths <= 120;

  const blockedUserIds = useMemo(() => getBlockedUserIdsSet(profile), [profile?.blockedUsers]);

  async function runSearch() {
    if (!user?.uid) {
      return;
    }
    setError(null);
    setBusy(true);
    try {
      if (mode === 'nearby') {
        const c = city.trim();
        const d = district.trim();
        if (!c || !d) {
          setError('İl ve ilçe gir veya profilinde doldur.');
          return;
        }
        const list = await searchNearbyUsers(c, d, user.uid);
        setResults(list.filter(u => !blockedUserIds.has(u.id)));
      } else {
        if (!canRunGeneral) {
          setError('0 ile 120 arasında bir ay sayısı gir.');
          return;
        }
        const list = await searchByTargetBabyAgeMonths(targetMonths, user.uid, 1);
        setResults(list.filter(u => !blockedUserIds.has(u.id)));
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Arama başarısız';
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  async function openChatWith(peerId: string, peerDisplayName: string) {
    if (user?.isAnonymous === true) {
      showGuestAlert(logout);
      return;
    }
    if (!user?.uid) {
      return;
    }
    const { chatId } = await ensureChat(user.uid, peerId);
    tabNavigation
      .getParent<NativeStackNavigationProp<MainStackParamList>>()
      ?.navigate('Chat', {
        chatId,
        otherUserId: peerId,
        peerName: peerDisplayName,
      });
  }

  return (
    <View className="flex-1 bg-white dark:bg-black" style={{ paddingTop: insets.top }}>
      <View className="flex-1 px-6 pt-6">
        {error ? (
          <View className="mb-5 rounded-2xl border border-zinc-200 bg-zinc-50 px-5 py-4 dark:border-zinc-800 dark:bg-zinc-900">
            <Text className="text-center text-sm text-zinc-800 dark:text-zinc-200">{error}</Text>
          </View>
        ) : null}

        <View className="mb-6 flex-row border-b border-zinc-200 dark:border-zinc-800">
          <TouchableOpacity
            activeOpacity={0.88}
            onPress={() => {
              setMode('nearby');
              setError(null);
            }}
            className={`mr-6 pb-2.5 ${mode === 'nearby' ? 'border-b-2 border-zinc-900 dark:border-zinc-100' : 'border-b-2 border-transparent'}`}>
            <Text
              className={`text-sm font-semibold ${
                mode === 'nearby' ? 'text-zinc-900 dark:text-zinc-100' : 'text-zinc-500 dark:text-zinc-500'
              }`}>
              Yakınımda
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            activeOpacity={0.88}
            onPress={() => {
              setMode('general');
              setError(null);
            }}
            className={`pb-2.5 ${mode === 'general' ? 'border-b-2 border-zinc-900 dark:border-zinc-100' : 'border-b-2 border-transparent'}`}>
            <Text
              className={`text-sm font-semibold ${
                mode === 'general' ? 'text-zinc-900 dark:text-zinc-100' : 'text-zinc-500 dark:text-zinc-500'
              }`}>
              Genel arama
            </Text>
          </TouchableOpacity>
        </View>

        {mode === 'nearby' ? (
          <>
            <Text className="mb-1.5 text-sm font-semibold text-zinc-900 dark:text-zinc-100">İl</Text>
            <View className="mb-4 border-b border-zinc-200 pb-1 dark:border-zinc-700">
              <TextInput
                className="py-2 text-sm text-zinc-900 dark:text-zinc-100"
                placeholder="İl"
                placeholderTextColor="#9ca3af"
                value={city}
                onChangeText={setCity}
              />
            </View>
            <Text className="mb-1.5 text-sm font-semibold text-zinc-900 dark:text-zinc-100">İlçe</Text>
            <View className="mb-4 border-b border-zinc-200 pb-1 dark:border-zinc-700">
              <TextInput
                className="py-2 text-sm text-zinc-900 dark:text-zinc-100"
                placeholder="İlçe"
                placeholderTextColor="#9ca3af"
                value={district}
                onChangeText={setDistrict}
              />
            </View>
            <Text className="mb-4 text-xs text-zinc-500">
              Varsayılan: profilindeki konum. İstersen değiştirebilirsin.
            </Text>
          </>
        ) : (
          <>
            <Text className="mb-2 text-sm font-semibold text-zinc-900 dark:text-zinc-100">
              Kaç aylık bebeklerin ebeveynlerini arıyorsun?
            </Text>
            <View className="mb-4 border-b border-zinc-200 pb-1 dark:border-zinc-700">
              <TextInput
                className="py-2 text-sm text-zinc-900 dark:text-zinc-100"
                placeholder="Örn. 8"
                placeholderTextColor="#9ca3af"
                keyboardType="number-pad"
                value={targetMonthsInput}
                onChangeText={t => {
                  setError(null);
                  setTargetMonthsInput(t);
                }}
              />
            </View>
            <Text className="mb-4 text-xs leading-5 text-zinc-500">
              Yaklaşık ±1 ay doğum bandında eşleşme (MVP). 0–120 ay arası gir.
            </Text>
          </>
        )}

        <TouchableOpacity
          activeOpacity={0.85}
          onPress={runSearch}
          disabled={busy}
          className={`mb-6 self-center rounded-full border border-zinc-900 bg-zinc-900 px-8 py-2.5 dark:border-zinc-100 dark:bg-zinc-50 ${
            busy ? 'opacity-60' : 'active:opacity-90'
          }`}>
          {busy ? (
            <ActivityIndicator color="#ffffff" />
          ) : (
            <Text className="text-center text-sm font-semibold text-white dark:text-zinc-950">Listele</Text>
          )}
        </TouchableOpacity>

        <FlatList
          data={results}
          keyExtractor={item => item.id}
          contentContainerStyle={{ paddingBottom: 120 + insets.bottom }}
          ListEmptyComponent={
            <Text className="mt-6 text-center text-sm text-zinc-500">
              Henüz sonuç yok. Yukarıdan arama yap.
            </Text>
          }
          renderItem={({ item }) => (
            <View className="mb-4 rounded-xl border border-zinc-200 bg-white px-4 py-3 dark:border-zinc-800 dark:bg-zinc-900">
              <Text className="text-base font-semibold text-zinc-900 dark:text-zinc-100">{item.displayName}</Text>
              <Text className="mt-1 text-xs text-zinc-500">
                {item.city}, {item.district}
              </Text>
              <Text className="mt-1 text-sm text-zinc-500">
                {mode === 'nearby'
                  ? user
                    ? formatFuzzyProximityLabel(item.city, item.district, user.uid, item.id)
                    : ''
                  : `Bebek ~${formatBabyAgeMonthsLabel(getBabyAgeInMonths(item.babyBirthDate))}`}
              </Text>
              <TouchableOpacity
                activeOpacity={0.85}
                onPress={() => {
                  if (user?.isAnonymous === true) {
                    showGuestAlert(logout);
                    return;
                  }
                  void impactLight();
                  void openChatWith(item.id, item.displayName);
                }}
                className="mt-3 self-start rounded-lg border border-zinc-900 bg-zinc-900 px-3 py-2 dark:border-zinc-50 dark:bg-zinc-50">
                <Text className="text-center text-xs font-semibold text-white dark:text-zinc-950">
                  Mesaj Gönder
                </Text>
              </TouchableOpacity>
            </View>
          )}
        />
      </View>
    </View>
  );
}
